import _ from 'lodash'
import { flow, get, isEqual, toFinite, isNil, reject, toString, map } from 'lodash/fp'
import moment from 'moment'
import { getToggles } from 'quick-quote/feature-toggles'
import { PAYMENT_TYPE, CREDIT_CARD_PAYMENT_METHOD } from 'e-submission/domain/data-model/constants'
import {
  getReceipts,
  getBankCode,
  getReceiptAmount,
  getReceiptType,
  getReceiptTransactionId,
} from 'e-submission/domain/data-model/form/selectors'
import { modelFactorToPaymentMode } from 'e-submission/sagas/policy-payload/coverageplan'

const bangkokProvinceObject = { text: 'กรุงเทพมหานคร', value: '010' }

export const addPrefixToAmphur = (amphur, province) => {
  if (_.isEmpty(amphur)) {
    return undefined
  }
  const amphurPrefix = _.isEqual(province, bangkokProvinceObject) ? 'เขต' : 'อ.'
  return `${amphurPrefix}${amphur}`
}
export const addPrefixToTambol = (tambol, province) => {
  if (_.isEmpty(tambol)) {
    return undefined
  }
  const tambolPrefix = _.isEqual(province, bangkokProvinceObject) ? 'แขวง' : 'ต.'
  return `${tambolPrefix}${tambol}`
}

export const creditCardSuffix = (creditCardNumber) => {
  return `xxxx-xxxx-xxxx-${creditCardNumber.substring(12)}`
}

export const quickPayStatus = {
  SUCCESS: (dateTime, creditCardNumber) => ({
    title: 'ผลการตรวจสอบการชำระเงิน',
    body: `<p>ชำระเงินเรียบร้อยเมื่อวันที่ ${dateTime.date} เวลา ${
      dateTime.time
    } น.<br/>ผ่านบัตรเครดิต ลงท้ายด้วย ${creditCardSuffix(creditCardNumber)}</p>`,
  }),
  PENDING: () => ({
    title: 'ผลการตรวจสอบการชำระเงิน',
    body: '<p>ไม่พบข้อมูลการชำระเงิน<br/>กรุณาตรวจสอบสถานะการชำระเบี้ยประกันกับผู้เอาประกันอีกครั้ง</p>',
  }),
  EXPIRED: (dateTime) => ({
    title: 'ผลการตรวจสอบการชำระเงิน',
    body: `<p>ลิงก์สำหรับชำระเบี้ยประกันหมดอายุแล้วเมื่อวันที่ ${dateTime.date} เวลา ${dateTime.time} น.<br/>กรุณากด ยืนยัน เพื่อสร้างลิงก์ชำระเบี้ยอีกครั้ง หรือ เปลี่ยนวิธีการชำระเบี้ย</p>`,
  }),
  ERROR: {
    title: 'ผลการตรวจสอบการชำระเงิน',
    body: '<p>ระบบตรวจสอบการชำระเบี้ยขัดข้อง กรุณากด ยืนยัน เพื่อกลับไปทำการ<br/>ตรวจสอบการชำระเงินอีกครั้ง</p>',
  },
}

export const ccpaStatus = {
  // SUCCESS: (dateTime, creditCardNumber) => ({
  //   title: 'ผลการตรวจสอบการชำระเงิน',
  //   body: `<p>ชำระเงินเรียบร้อยเมื่อวันที่ ${dateTime.date} เวลา ${
  //     dateTime.time
  //   } น.<br/>ผ่านบัตรเครดิต ลงท้ายด้วย ${creditCardSuffix(creditCardNumber)}</p>`,
  // }),
  PENDING: () => ({
    title: 'ผลการสมัครหักค่าเบี้ยประกันอัตโนมัติผ่านบัตรเครดิต',
    body:
      '<p>ไม่พบข้อมูลการสมัคร<br/>กรุณาตรวจสอบสถานะการสมัครหักค่าเบี้ยอัตโนมัติ<br/>ผ่านบัตรเครดิตผู้เอาประกันอีกครั้ง</p>',
  }),
  EXPIRED: (dateTime) => ({
    title: 'ผลการสมัครหักค่าเบี้ยประกันอัตโนมัติผ่านบัตรเครดิต',
    body: `<p>ลิงก์สำหรับการสมัครเพื่อหักค่าเบี้ยประกันอัตโนมัติ หมดอายุแล้วเมื่อวันที่ ${dateTime.date} เวลา ${dateTime.time} น.<br/>กรุณากด ยืนยัน เพื่อสร้างลิงก์สมัครอีกครั้ง หรือ เปลี่ยนวิธีการสมัคร</p>`,
  }),
  ERROR: {
    title: 'ผลการสมัครหักค่าเบี้ยประกันอัตโนมัติผ่านบัตรเครดิต',
    body: '<p>ขออภัย ขณะนี้ระบบไม่สามารถทำการตรวจสอบผลการสมัครได้<br/>กรุณากดยืนยันเพื่อไปยังขั้นตอนถัดไป</p>',
  },
}

export const checkAccountStatus = {
  SUCCESS_DCA: {
    title: 'การรับผลประโยชน์ตามกรมธรรม์',
    body: 'กรุณายืนยันหากท่านต้องการรับผลประโยชน์ตามกรมธรรม์<br/>และสมัครหักค่าเบี้ยประกันผ่านบัญชีนี้',
  },
  SUCCESS_ATP: {
    title: 'การสมัครหักค่าเบี้ยประกันอัตโนมัติ',
    body: 'กรุณายืนยันหากท่านต้องการสมัครหักค่าเบี้ยประกัน<br/>และรับผลประโยชน์ตามกรมธรรม์ผ่านบัญชีนี้',
  },
  SUCCESS_ONLY_DCA: {
    title: 'กรุณาตรวจสอบข้อมูลบัญชีธนาคาร',
    body: 'กรุณายืนยันหากท่านต้องการรับผลประโยชน์ตามกรมธรรม์ผ่านบัญชีนี้',
  },
  SUCCESS_ONLY_ATP: {
    title: 'กรุณาตรวจสอบข้อมูลบัญชีธนาคาร',
    body: 'กรุณายืนยันหากท่านต้องการสมัครหักค่าเบี้ยประกันผ่านบัญชีนี้',
  },
  MISMATCH: {
    title: 'กรุณาตรวจสอบข้อมูลบัญชีธนาคาร',
    body: 'เนื่องจากข้อมูลไม่ตรงกับที่ท่านระบุ ท่านต้องการแก้ไขข้อมูลหรือไม่',
  },
  INVALID: {
    title: 'กรุณาตรวจสอบข้อมูลบัญชีธนาคาร',
    body: 'ตรวจสอบเลขที่บัญชีหรือสถานะบัญชีไม่ถูกต้อง<br/>โปรดระบุเลขที่บัญชีใหม่อีกครั้ง',
  },
  ERROR: {
    title: 'กรุณาตรวจสอบข้อมูลบัญชีธนาคาร',
    body: 'ขณะนี้ระบบไม่สามารถตรวจสอบข้อมูลบัญชีธนาคารได้<br/>กรุณาตรวจสอบชื่อบัญชีให้ตรงกับบัญชีธนาคารของท่าน',
  },
}

export const convertMomentDateToObject = (date) => {
  if (!date) return {}

  const momentDate = moment(date).format('DD/MM/YYYY HH:mm')
  return {
    date: momentDate.split(' ')[0],
    time: momentDate.split(' ')[1],
  }
}

export const checkMatchValue = (str1, str2) => {
  if (typeof str1 !== 'string' || typeof str2 !== 'string') {
    return false
  }
  const formattedStr1 = str1.trim().toLowerCase()
  const formattedStr2 = str2.trim().toLowerCase()

  return formattedStr1 === formattedStr2
}

export const checkNameOFInsuredPayer = (app, isShowDCA) => {
  try {
    let isHasInsureAndPayer = 'DCA' in app ? true : false
    if (isHasInsureAndPayer) {
      if (
        checkMatchValue(app.DCA.firstName, app.ATP.firstName) &&
        checkMatchValue(app.DCA.lastName, app.ATP.lastName)
      ) {
        return true
      } else {
        return false
      }
    } else {
      const typeOfPayer = _.has(app, 'payer') ? 'payer' : 'insured'
      if (
        isShowDCA &&
        checkMatchValue(_.get(app, `${typeOfPayer}.firstName`, ''), app.ATP.firstName) &&
        checkMatchValue(_.get(app, `${typeOfPayer}.lastName`, ''), app.ATP.lastName)
      ) {
        return true
      } else {
        return false
      }
    }
  } catch (error) {
    return false
  }
}

export const getATPDefaultRelation = (app) => {
  const atpInfo = {
    name: get('ATP.firstName', app),
    lastName: get('ATP.lastName', app),
  }
  const insuredInfo = {
    name: get('insured.firstName', app),
    lastName: get('insured.lastName', app),
  }
  const payerInfo = {
    name: get('payer.firstName', app),
    lastName: get('payer.lastName', app),
  }
  const relationshipToInsured = get('payer.relationshipToInsured.value', app)
  const isOwnerRelation = ['SPOUSE', 'PARENT'].includes(relationshipToInsured)

  if (isEqual(atpInfo, insuredInfo)) {
    return 'SELF'
  } else if (isEqual(atpInfo, payerInfo) && isOwnerRelation) {
    return 'PAYER'
  } else {
    return 'OTHERS'
  }
}

export const attachPaidPaymentIntoReceipts = (respond, receipts, typeOfPayment, qrStatusChecked = true) => {
  if (!respond) return receipts
  const _receipts = _.cloneDeep(receipts)
  const indexOfNetBank = _receipts.findIndex((receipt) => receipt?.type === typeOfPayment)
  _receipts[indexOfNetBank] = {
    ..._receipts[indexOfNetBank],
    receiptNo: respond?.ecbrNo,
    ecbrStatus: respond?.ecbrStatus,
    ecbrDate: respond?.ecbrIssueDate,
    paid: true,
    currency: 'thb',
    qrStatusChecked,
  }
  _receipts[indexOfNetBank].bypassPayingCounter = _receipts[indexOfNetBank].qrStatusChecked === false ? true : false
  _receipts[indexOfNetBank].amount = _receipts[indexOfNetBank].totalFirstPremiumPayment
  return _receipts
}

export const createDialogPropsForBypassToManuallyAttachSlip = () => {
  const dialogProps = { onlyConfirm: true }
  const message = {
    title: 'ผลการตรวจสอบการชำระเงิน',
    body: '<p>ระบบตรวจสอบการชำระเบี้ยขัดข้อง กรุณากด ยืนยัน เพื่อกลับไปทำการ<br/>ตรวจสอบการชำระเงินอีกครั้ง</p>',
  }
  if (getToggles().ENABLE_QR_PAYMENT_STATUS) {
    dialogProps.confirmText = 'แนบสลิป'
    dialogProps.confirmYesWithAction = 'BYPASS_CREDIT_CARD_TO_SIGNING_PAGE'
    message.body = '<p>ขณะนี้ระบบไม่สามารถตรวจสอบการชำระเงินได้<br/>กรุณาแนบหลักฐานการชำระเงินด้วยตนเอง</p>'
  }
  return {
    dialogProps,
    message,
  }
}

export const receiptWithPaid = (applicationReceipts = []) => {
  const receiptPaid = applicationReceipts && applicationReceipts.filter((receipt) => receipt?.paid === true)
  return receiptPaid[0]
}

export const checkAppReceiptHaveMatchUnsigned = (unsignedPaymentReceipt, receipt) => {
  const receipts = {}
  let bypassPayingCounter
  if (unsignedPaymentReceipt?.bypassPayingCounter) {
    bypassPayingCounter = unsignedPaymentReceipt?.bypassPayingCounter
  } else {
    bypassPayingCounter = receipt?.bypassPayingCounter
  }
  switch (unsignedPaymentReceipt?.type) {
    case PAYMENT_TYPE.NETBANK:
      // Do not map type
      receipts.type = PAYMENT_TYPE.NETBANK // FIX TYPE NET BANK NOT CARE QR CODE STAMP ON UNSIGNED-PAYMENT-RECEIPT
      receipts.amount = parseFloat(unsignedPaymentReceipt?.amount)
      receipts.currency = unsignedPaymentReceipt?.currency
      receipts.bankCode = getBankCode(receipt) || ''
      receipts.ref2 = unsignedPaymentReceipt?.ref2
      receipts.bypassPayingCounter = bypassPayingCounter
      receipts.text = 'MATCH NET BANK & QR CODE'
      break
    case PAYMENT_TYPE.CREDITCARD:
    case PAYMENT_TYPE.QUICKPAY:
      receipts.type = PAYMENT_TYPE.CREDITCARD // FIX TYPE CREDIT CARD
      receipts.amount = parseFloat(unsignedPaymentReceipt?.amount)
      receipts.currency = unsignedPaymentReceipt?.currency
      receipts.transactionId = unsignedPaymentReceipt?.transactionId
      receipts.provider = unsignedPaymentReceipt?.provider
      receipts.merchantId = unsignedPaymentReceipt?.merchantId
      receipts.maskCreditCardNumber = unsignedPaymentReceipt?.maskCreditCardNumber
      receipts.text = 'MATCH CREDIT CARD & QUICK PAY'
      break
    default:
      receipts.amount = parseFloat(unsignedPaymentReceipt?.amount)
      receipts.currency = unsignedPaymentReceipt?.currency
      receipts.bankCode = getBankCode(receipt) || ''
      receipts.ref2 = unsignedPaymentReceipt?.ref2
      receipts.bypassPayingCounter = unsignedPaymentReceipt?.bypassPayingCounter
      receipts.text = 'TYPE UNSIGNED NOT MATCH MAY BE QR CODE'
  }

  return receipts
}

export const cloneDataUnsignedPaymentReceiptToNetbank = (cloneAppReceiptByType) => {
  console.info('START CLONE DATA UNSIGNED PAYMENT RECRIPT TO NETBANK')
  // NETBANK -- You must merge object from application.receipt[3]
  return {
    type: PAYMENT_TYPE.NETBANK,
    amount: parseFloat(cloneAppReceiptByType?.amount),
    currency: cloneAppReceiptByType?.currency,
    bankCode: cloneAppReceiptByType?.bankCode,
    ref2: cloneAppReceiptByType?.ref2,
    bypassPayingCounter: cloneAppReceiptByType?.bypassPayingCounter,
    paid: true,
    isCloned: true,
  }
}

export const cloneDataReceiptPaidForQuickpayError = (receiptPaid) => {
  console.info('START CLONE DATA UNSIGNED PAYMENT RECRIPT QUICKPAY TO CREDIT CARD')

  return {
    type: PAYMENT_TYPE.CREDITCARD,
    totalFirstPremiumPayment: parseFloat(receiptPaid?.totalFirstPremiumPayment),
    receiptNo: receiptPaid?.receiptNo,
    qrStatusChecked: receiptPaid?.qrStatusChecked,
    ecbrStatus: receiptPaid?.ecbrStatus,
    amount: parseFloat(receiptPaid?.amount),
    currency: receiptPaid?.currency,
    bankReceiptDate: receiptPaid?.bankReceiptDate,
    paid: true,
    isCloned: true,
    bypassPayingQuickpay: receiptPaid?.bypassPayingQuickpay,
  }
}

export const cloneDataUnsignedPaymentReceiptToCreditCard = (cloneAppReceiptByType) => {
  console.info('START CLONE DATA UNSIGNED PAYMENT RECRIPT TO CREDIT CARD')
  // CREDIT CARD -- You must merge object from application.receipt[2]
  return {
    type: PAYMENT_TYPE.CREDITCARD,
    amount: parseFloat(cloneAppReceiptByType?.amount),
    totalFirstPremiumPayment: parseFloat(cloneAppReceiptByType?.amount),
    currency: cloneAppReceiptByType?.currency,
    transactionId: cloneAppReceiptByType?.transactionId,
    provider: cloneAppReceiptByType?.provider,
    merchantId: cloneAppReceiptByType?.merchantId,
    maskCreditCardNumber: cloneAppReceiptByType?.maskCreditCardNumber,
    paid: true,
    isCloned: true,
  }
}

export const getPaymentActualAmount = (receipt) => {
  return !_.isEmpty(receipt?.amount) && receipt?.amount
    ? parseFloat(receipt?.amount)
    : parseFloat(receipt?.totalFirstPremiumPayment)
}

export const getProviderFromUnsignPayment = (provider) => {
  let creditCardMethod = ''
  if (provider === 'Quickpay') {
    creditCardMethod = CREDIT_CARD_PAYMENT_METHOD.QUICKLINK
  } else if (provider === 'Cybersource') {
    creditCardMethod = CREDIT_CARD_PAYMENT_METHOD.ON_AZ
  }
  return creditCardMethod
}

const mapWithKey = map.convert({ cap: false })

export const getReceiptsWithAmountAndType = (app) => {
  const receipts = flow(
    getReceipts,
    mapWithKey((receipt, key) => {
      let response = {
        ...receipt,
        key,
        amount: toString(getReceiptAmount(receipt)),
        type: getReceiptType(receipt),
        transactionId: getReceiptTransactionId(receipt),
        currency: 'thb',
      }
      return response
    }),
    reject(({ amount }) => toFinite(amount) === 0),
    reject(({ type }) => isNil(type))
  )(app)

  return receipts
}

export const createPayloadForGetPaymentStatusAndEcbrNo = (app) => {
  const _payerType = _.get(app, 'payerInfo.payerType', 'insured')

  const ecbrPayerType = {
    insured: 'INS',
    payer: 'OWN',
    other: 'OTHER',
  }

  const payerType = ecbrPayerType[_payerType]

  let payerName = `${_.get(app, 'insured.firstName', '')} ${_.get(app, 'insured.lastName', '')}`

  if (payerType === 'OTHER') {
    payerName = `${_.get(app, 'payerInfo.firstName', '')} ${_.get(app, 'payerInfo.lastName', '')}`
  } else if (payerType === 'OWN') {
    payerName = `${_.get(app, 'payer.firstName', '')} ${_.get(app, 'payer.lastName', '')}`
  }
  const payerRelationshipType = {
    บิดามารดา: '01',
    คู่สมรส: '02',
    บุตร: '03',
  }

  const _payerRelationShip = _.get(app, 'payer.relationshipToInsured.text', '')
  const paymentMode = modelFactorToPaymentMode(_.get(app, 'quickQuote.coveragePlan.selectedModelFactorID', ''))

  return {
    ref2: _.get(app, 'receipts[3].ref2', ''),
    payerName,
    payerType,
    planCode: _.get(app, 'quickQuote.selectedDisplayProduct.basicPlanCode', ''),
    policyNo: _.get(app, 'policyId', ''),
    agentCode: _.get(app, 'quickQuote.agent.advisorId', ''),
    payerRelationShip: payerRelationshipType[_payerRelationShip] || _payerRelationShip,
    toCustomerEmail: _.get(app, 'insured.email', ''),
    toCustomerPhone: _.get(app, 'insured.mobileNumber', ''),
    toAgentEmail: _.get(app, 'quickQuote.agent.contactMethods.emailContact[1].url', ''),
    toAgentPhone: _.get(app, 'quickQuote.agent.contactMethods.phoneContacts[0].localNumber', ''),
    insuredFirstName: _.get(app, 'insured.firstName', ''),
    insuredLastName: _.get(app, 'insured.lastName', ''),
    sumInsuredAmount: _.get(
      app,
      'quickQuote.coveragePlan.selectedBasicPlan.sumAssured.value',
      _.get(app, 'quickQuote.coveragePlan.selectedBasicPlan.sumAssured', '')
    ),
    citizenId: _.get(app, 'insured.idNo.citizenId', _.get(app, 'insured.idNo.passport', '')),
    paymentMode,
    agentName: _.get(app, 'quickQuote.agent.fullName', ''),
    telephone: _.get(app, 'insured.mobileNumber', ''),
  }
}

export const createPayloadForEcbrFilesApi = (policyId, ecbrNo, bankCode, ref2, isBypassPayingCounter) => {
  return {
    policyId,
    ecbrNo,
    bankCode: bankCode,
    ref2,
    bypassPayingCounter: isBypassPayingCounter ? true : false,
    isIgnoreDownloadFile: true,
  }
}

export const shouldApplyEkycSourceData = (prefix, app) => {
  const enableUpdateSourceData = _.get(app, 'ui.isUpdateSourceData', true)
  const verificationDetail = _.get(app, `ekycInfo.${prefix}.verificationDetail`, {})
  const frontCardResult = _.get(verificationDetail, 'frontIdCardResult.edits', {})
  const isForeignerEkyc = _.isEmpty(frontCardResult)
  const isDopaVerified = _.get(verificationDetail, 'dopaResult.verified', false)
  const isDopaRequestFailed = _.get(verificationDetail, 'dopaResult.dopaRequestFailed', false)
  const foreignerVerified = _.get(verificationDetail, 'passportResult.verified', false)

  return (
    enableUpdateSourceData &&
    (isForeignerEkyc ? foreignerVerified : isDopaRequestFailed === false && isDopaVerified === true)
  )
}

export const isUsableEkycValue = (value) =>
  _.isString(value) ? !_.isEmpty(_.trim(value)) && value !== '-' : !_.isNil(value)

export const getEkycResult = (ekycInfo, insuredOrPayer) => {
  const ekycInfoData = _.get(ekycInfo, insuredOrPayer, {})
  const verificationDetail = _.get(ekycInfoData, 'verificationDetail', {})
  const frontCardResult = _.get(verificationDetail, 'frontIdCardResult.edits', {})
  const passportResult = _.get(verificationDetail, 'passportResult.edits', {})
  const isForiegnerEkyc = _.isEmpty(frontCardResult)

  const ekycResult = isForiegnerEkyc
    ? {
        firstName: isUsableEkycValue(passportResult.firstName) ? passportResult.firstName : undefined,
        lastName: isUsableEkycValue(passportResult.lastName) ? passportResult.lastName : undefined,
      }
    : {
        title: isUsableEkycValue(frontCardResult.titleTh) ? frontCardResult.titleTh : undefined,
        firstName: isUsableEkycValue(frontCardResult.firstNameTh) ? frontCardResult.firstNameTh : undefined,
        lastName: isUsableEkycValue(frontCardResult.lastNameTh) ? frontCardResult.lastNameTh : undefined,
      }

  return ekycResult
}

export const getTitleValue = (title) => {
  if (!title) {
    return ''
  }

  switch (title) {
    case 'นาย':
    case 'เด็กชาย':
    case 'ด.ช.':
      return 'mr'
    case 'นาง':
      return 'mrs'
    case 'นางสาว':
    case 'น.ส.':
    case 'เด็กหญิง':
    case 'ด.ญ.':
      return 'miss'
    default:
      return 'other'
  }
}
